// A row that follows the finger: drag right to run `start`, left to run `end`. Past the commit
// distance (or a quick flick) on release the action runs and the row springs back; short of it the
// row just returns. Both actions must also exist as taps (ListRow's menu), never gesture-only.
import { useState } from "react";
import { View } from "tamagui";
import { Body } from "./text";
import { Row } from "./layout";
import { haptic } from "./gestures/haptics";
import type { RowAction } from "./gestures/actions";
import { GestureArea } from "./gestures/GestureArea";
import { Icon } from "./Icon";

const COMMIT_PX = 96;
const FLICK_PX_PER_MS = 0.5;
const MIN_FLICK_PX = 40;
/** How far past the action's width a drag may stretch, so it feels attached. */
const STRETCH_PX = 140;

export interface SwipeRowProps {
  /** Revealed by dragging right. */
  start?: RowAction | undefined;
  /** Revealed by dragging left. */
  end?: RowAction | undefined;
  children: React.ReactNode;
}

function Reveal({ action, side }: { action: RowAction; side: "start" | "end" }): React.JSX.Element {
  const danger = action.tone === "danger";
  return (
    <Row
      position="absolute"
      top={0}
      bottom={0}
      // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
      {...(side === "start"
        ? { left: 0, justifyContent: "flex-start" }
        : { right: 0, justifyContent: "flex-end" })}
      width="100%"
      paddingHorizontal="5"
      gap="2"
      backgroundColor={danger ? "errorBg" : "accentSoft"}
      aria-hidden
    >
      {action.icon !== undefined && (
        <Icon name={action.icon} size={20} tone={danger ? "error" : "accent"} />
      )}
      <Body weight="strong" tone={danger ? "error" : "accent"}>
        {action.label}
      </Body>
    </Row>
  );
}

export function SwipeRow({ start, end, children }: SwipeRowProps): React.JSX.Element {
  const [dx, setDx] = useState(0);
  const [dragging, setDragging] = useState(false);

  if (start === undefined && end === undefined) return <>{children}</>;

  const clamp = (value: number): number => {
    const min = end === undefined ? 0 : -STRETCH_PX;
    const max = start === undefined ? 0 : STRETCH_PX;
    return Math.min(max, Math.max(min, value));
  };

  return (
    <View position="relative" overflow="hidden" borderRadius="2">
      {start !== undefined && dx > 0 && <Reveal action={start} side="start" />}
      {end !== undefined && dx < 0 && <Reveal action={end} side="end" />}
      <GestureArea
        axis="x"
        backgroundColor="bg"
        onDrag={({ dx: moved, vx, active }) => {
          setDragging(active);
          if (active) {
            setDx(clamp(moved));
            return;
          }
          const committed =
            Math.abs(moved) >= COMMIT_PX ||
            (Math.abs(vx) >= FLICK_PX_PER_MS && Math.abs(moved) >= MIN_FLICK_PX);
          const action = moved > 0 ? start : end;
          setDx(0);
          if (committed && action !== undefined && action.disabled !== true) {
            haptic("commit");
            action.onSelect();
          }
        }}
        style={{
          transform: `translateX(${dx}px)`,
          transition: dragging ? "none" : "transform var(--durBase) ease-out",
        }}
      >
        {children}
      </GestureArea>
    </View>
  );
}
